import { Component, computed, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TegelModule } from '@scania/tegel-angular-17';
import { RouterLink } from '@angular/router';
import { NotificationListComponent, Notification } from '@/app/components/notification-list/notification-list.component';
import { getPath, PathUnit } from '@/app/consts/path.const';
import { LandingPageService } from './landing-page.service';
import { SubscribePayload, SubscribeTypes } from './landing-page.interface';
import { finalize } from 'rxjs';
import { AuthService } from '@/app/core/services/auth.service';
import { RoleAccessDescMapping, RoleEnum } from '@/app/consts/role.const';
import { getQuickLinksByRole } from '@/app/consts/app-links.config';
import { NotifResponseItem } from '@/app/consts/notif.const';
import { NotificationService } from '../notifications/notification.service';

interface QuickLink {
  path: string;
  iconName?: string;
  label: string;
}

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [RouterLink, CommonModule, TegelModule, NotificationListComponent ],
  templateUrl: './landing-page.component.html',
  styleUrls: ['./landing-page.component.scss'],
  host: {
    class: 'tds-u-h-100 tds-u-flex',
    style: 'overflow-y: auto;padding: 3px;'
  },
  providers: [LandingPageService]
})
export class LandingPageComponent {
  landingPageService = inject(LandingPageService);
  authService = inject(AuthService);
  notifyBroadcast = inject(NotificationService);
  showNotificationCard = false;
  notificationList = signal<Notification[]>([]);
  subscribeTypes = [{
    key: SubscribeTypes.information,
    name: 'Information',
    value: SubscribeTypes.information,
  }, {
    key: SubscribeTypes.failures,
    name: 'System Failures',
    value: SubscribeTypes.failures,
  }, {
    key: SubscribeTypes.warnings,
    name: 'Warnings',
    value: SubscribeTypes.warnings,
  }, {
    key: SubscribeTypes.updates,
    name: 'Updates',
    value: SubscribeTypes.updates,
  }, {
    key: SubscribeTypes.softwareRelease,
    name: 'Software Release',
    value: SubscribeTypes.softwareRelease,
  }]
  selectedSubscribeTypes = new Map<SubscribeTypes | undefined, boolean | undefined>();
  userSubsTypes = this.landingPageService.userSubsTypes;
  isLoading = signal(false);
  roles = signal<string[]>([]);
  userRoleEnum = RoleEnum;

  notificationsRealTime = this.notifyBroadcast.notifications;
  firstRender = true;
  connectionState = this.notifyBroadcast.connectionState;

  brandAccessList = [{
    type: 'Scania',
    cardHeader: "Scania",
    cardDescHtml: `
      <div>
        Need access to CoreAssist, you can order access through <a href="https://iga.scania.com" target="_blank">https://iga.scania.com</a>. Below are available roles. To know more about what role you should apply visit ${this.generateRequestAccessHerfElement()}
      </div>
      <ul class="no-list-style">
        <li>CoreAssist_Prod_Basic</li>
        <li>CoreAssist_Prod_Extended</li>
        <li>CoreAssist_Prod_Complete</li>
        <li>CoreAssist_Prod_Super_User</li>
      </ul>
    `,
  },{
    type: 'Scania CN / Hero',
    cardHeader: "Scania CN / Hero",
    cardDescHtml: `
      <div>
        Need access to CoreAssist, you can order access through <a href="https://iga.scania.com" target="_blank">https://iga.scania.com</a>. Below are available roles. To know more about what role you should apply visit ${this.generateRequestAccessHerfElement()}
      </div>
      <ul class="no-list-style">
        <li>CoreAssist_Prod_Basic_CN</li>
        <li>CoreAssist_Prod_Extended_CN</li>
        <li>CoreAssist_Prod_Complete_CN</li>
        <li>CoreAssist_Prod_Super_User_CN</li>
      </ul>
    `,
  },{
    type: 'International',
    cardHeader: "International",
    cardDescHtml: `
      <div>
        Need access to CoreAssist, you can order access through <a href="https://sailpoint.navistar.com" target="_blank">https://sailpoint.navistar.com</a>. Below are available roles. To know more about what role you should apply visit ${this.generateRequestAccessHerfElement()}
      </div>
      <ul class="no-list-style">
        <li>CoreAssist-Basic</li>
        <li>CoreAssist-Extended</li>
        <li>CoreAssist-Complete</li>
        <li>CoreAssist-Super-User</li>
      </ul>
    `,
  }]
  constructor() { 
    effect(() => {
      if(this.userSubsTypes()) {
        const userSubsTypes = this.userSubsTypes();
        this.selectedSubscribeTypes.set(SubscribeTypes.updates, userSubsTypes?.updates);
        this.selectedSubscribeTypes.set(SubscribeTypes.warnings, userSubsTypes?.warnings);
        this.selectedSubscribeTypes.set(SubscribeTypes.failures, userSubsTypes?.failures);
        this.selectedSubscribeTypes.set(SubscribeTypes.information, userSubsTypes?.information);
        this.selectedSubscribeTypes.set(SubscribeTypes.softwareRelease, userSubsTypes?.softwareRelease);
      }

    });

    effect(() => {
      let notificationWS = this.notificationsRealTime();
      if(notificationWS && !this.firstRender) this.getNotificationList();
    })

    if(this.firstRender) {
      this.firstRender = false;
      this.getNotificationList();
    }
  }
  
  ngOnInit() {
    this.roles.set(this.authService.getCurrentUserRoles());
  }

  ngAfterViewInit() {
    // Create a MutationObserver to watch for changes in the DOM
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        // Check for added nodes
        mutation.addedNodes.forEach((node) => {
          if (
            node instanceof HTMLElement &&
            node.id === 'CardItem'
          ) {
            // Found CardItem, execute your logic
            this.showNotificationCard = true;
            // If you only need to listen once, disconnect the observer
            observer.disconnect();
          }
        });
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // If the CardItem already exists when the page initializes, it should be compatible
    const cardItem = document.getElementById('CardItem');
    if (cardItem) {
      this.showNotificationCard = true;
      observer.disconnect();
    }
  }

  getNotificationList() {
    this.landingPageService.fetchNotificationListForUserSide()
    .pipe()
    .subscribe({
      next: (res: NotifResponseItem[]) => {
        const notificationList: Notification[] = res?.map(notif => {
          const { id, messageType, message, title } = notif || {};
          return {
            id,
            messageType,
            message: message,
            title,
            minimal: true,
            startAt: notif.startAt,
            expirationAt: notif.expirationAt,
          }
        })
        this.notificationList.set(notificationList);
        // this.fillNotifCardWidthAndHeightStyle();
      },
      error: err => {
        
      }
    })

  }

  handleCheckSubscribe(e: CustomEvent<{ checkboxId: string; checked: boolean; indeterminate: boolean; value?: string; }>) {
    const { detail: { checked, value } = {} } = e || {};
    this.selectedSubscribeTypes.set(value as unknown as SubscribeTypes, checked);
  }

  subscribeNotifyEmail() {
    if(this.isLoading()) return;
    this.isLoading.set(true);

    let payload: SubscribePayload = {
      id: this.userSubsTypes()?.id,
      failures: this.selectedSubscribeTypes.get(SubscribeTypes.failures) || false,
      information: this.selectedSubscribeTypes.get(SubscribeTypes.information) || false,
      warnings: this.selectedSubscribeTypes.get(SubscribeTypes.warnings) || false,
      updates: this.selectedSubscribeTypes.get(SubscribeTypes.updates) || false,
      softwareRelease: this.selectedSubscribeTypes.get(SubscribeTypes.softwareRelease) || false,
    }
    this.landingPageService.subscribeNotificationEmail(payload)
    .pipe(finalize(() => {
      this.isLoading.set(false);
    }))
    .subscribe({
      next: (res) => {

      }
    })
  }

  myAccessTitle = computed(() => {
    if (this.roles().length === 0) {
      return `You don't have any roles to access Core Assist. Please request access to get started.`;
    } else {
      // if user has multiple roles, just list them all but remove guest, but if user only has guest, then show guest
      const rolesToShow = this.roles().length > 1 ? this.roles().filter(role => role !== RoleEnum.Guest) : this.roles();
      return `You are having ${rolesToShow.join(',')} Access. With your access you can do below.`;
    }
  })

  // choose the highest role to show desc and will include desc from lower roles
  myAccessDesc = computed(() => {
    let descs: string[] = [];
    if(this.roles().includes(RoleEnum.SuperUser)) {
      descs = RoleAccessDescMapping[RoleEnum.SuperUser]
    } else if(this.roles().includes(RoleEnum.Complete)) {
      descs = RoleAccessDescMapping[RoleEnum.Complete]
    }  else if(this.roles().includes(RoleEnum.Extended)) {
      descs = RoleAccessDescMapping[RoleEnum.Extended]
    }  else if(this.roles().includes(RoleEnum.Basic)) {
      descs = RoleAccessDescMapping[RoleEnum.Basic]
    } else if(this.roles().includes(RoleEnum.Guest)) {
      descs = RoleAccessDescMapping[RoleEnum.Guest]
    } else {
      descs = []; // no role
    }
    return descs;
  })

  quickLinks = computed(() => {
    const roleOrder = Object.values(RoleEnum);
    const userRoles = this.roles() as unknown as RoleEnum[];
    if (userRoles.length) {
      const primary = userRoles.reduce((prev, curr) => {
        const prevIdx = roleOrder.indexOf(prev as RoleEnum);
        const currIdx = roleOrder.indexOf(curr as RoleEnum);
        return currIdx < prevIdx ? curr as RoleEnum : prev as RoleEnum;
      }, userRoles[0] as RoleEnum);
      return getQuickLinksByRole(primary)
        .filter(l => !!l.route || !!l.quickLinkRoute)
        .map(l => ({ label: l.label, route: `/${l.route || l.quickLinkRoute}`, iconName: l.icon }));
    }
    // fallback: only Request Access
    return [
      { label: 'Request Access', route: `/${getPath(PathUnit.RequestAccess)}` }
    ];
  });

  generateRequestAccessHerfElement() {
    const baseUrl = window.location.origin;
    const requestAccessPath = getPath(PathUnit.RequestAccess);
    const href = `${baseUrl}/${requestAccessPath}`;
    return `<a href="${href}" target="_self">Request Access</a>`;
  }

}